<template>
	<div
		:style="
			maxHeight && {
				'max-height': `${maxHeight}`
			}
		"
		:class="['infinite-scroller', classList]"
		ref="scrollDiv"
	>
		<slot name="scrollDiv"></slot>
		<portal
			:disabled="portalDisabled"
			:slim="true"
			:to="loaderPortalTarget"
		>
			<div
				class="infinite-scroller__container-elements"
				v-if="initialMoreData && (loading || !moreData) && !scrollingUp"
				data-testid="endResultContainer"
			>
				<div class="infinite-scroller__loading" v-if="loading">
					<Loader variant="dots" />
				</div>
				<div class="infinite-scroller__end-result" v-if="!moreData">
					<span class="infinite-scroller__text">
						No more {{ objectTypeName }}.
					</span>
					<img
						class="infinite-scroller__img"
						:src="endResultsSvg"
						alt="End of Results Graphic - Road Sign"
						v-if="showEndResultImg"
					/>
				</div>
			</div>
		</portal>
	</div>
</template>

<script>
import Loader from '@/components/visual/loader/loader.vue';
import EndResultsSvg from '@/assets/end-results.svg';

export default {
	name: 'InfiniteScroller',
	components: {
		Loader
	},
	data() {
		return {
			scrollElementData: undefined,
			wheelElementData: undefined,

			//if there weren't more results on initial load, don't show the end of results message
			initialMoreData: this.moreData,
			scrollingUp: false,
			endResultsSvg: EndResultsSvg
		};
	},
	props: {
		objectTypeName: {
			type: String,
			default: 'results'
		},
		moreData: {
			type: Boolean,
			default: true
		},
		loading: {
			type: Boolean,
			default: false
		},
		scrollDirection: {
			type: String,
			default: 'vertical'
		},
		hasMoreResults: {
			type: Boolean,
			default: false
		},
		classList: {
			type: [String, Array],
			default: undefined
		},
		showEndResultImg: {
			type: Boolean,
			default: false
		},
		/**
		 * Override the element that the wheel event is attached to
		 */
		wheelElement: {
			type: HTMLElement,
			default: undefined
		},
		/**
		 * Override the element that the scroll event is attached to
		 */
		scrollElement: {
			type: HTMLElement,
			default: undefined
		},
		maxHeight: {
			type: String
		},
		additionalScrollerPadding: {
			type: Number,
			default: 0
		},
		loaderPortalTarget: {
			type: String,
			default: undefined
		}
	},
	watch: {
		wheelElement: {
			inmediate: true,
			handler(val) {
				if (val && this.wheelElementData !== val) {
					this.wheelElementData.removeEventListener(
						'wheel',
						this.onWheel
					);
					this.wheelElementData = val;
					this.wheelElementData.addEventListener(
						'wheel',
						this.onWheel
					);
				}
			}
		},
		scrollElement: {
			inmediate: true,
			handler(val) {
				if (val && this.scrollElementData !== val) {
					this.scrollElementData.removeEventListener(
						'scrollend',
						this.onScroll
					);
					this.scrollElementData = val;
					this.scrollElementData.addEventListener(
						'scrollend',
						this.onScroll
					);
				}
			}
		}
	},
	mounted() {
		this.scrollElementData = this.scrollElement || this.$refs.scrollDiv;

		if (this.scrollElementData) {
			this.scrollElementData.removeEventListener(
				'scrollend',
				this.onScroll
			);
			this.scrollElementData.addEventListener('scrollend', this.onScroll);
		}

		this.wheelElementData =
			this.wheelElement || this.$refs.scrollDiv.firstElementChild;

		if (this.wheelElementData) {
			this.wheelElementData.removeEventListener('wheel', this.onWheel);
			this.wheelElementData.addEventListener('wheel', this.onWheel);
		}
	},
	beforeDestroy() {
		if (this.wheelElementData) {
			this.wheelElementData.removeEventListener('wheel', this.onWheel);
		}

		if (this.scrollElementData) {
			this.scrollElementData.removeEventListener('scroll', this.onScroll);
		}
	},
	computed: {
		portalDisabled() {
			return !this.loaderPortalTarget;
		}
	},
	methods: {
		windowBoundary() {
			const el = this.wheelElementData;
			const scrollDiv = this.scrollElementData;
			let result = false;
			if (el) {
				if (this.scrollDirection == 'vertical') {
					const height =
						el.scrollHeight -
						(scrollDiv.offsetHeight +
							this.additionalScrollerPadding);
					result = scrollDiv.scrollTop >= height;
				} else if (this.scrollDirection == 'horizontal') {
					const width =
						el.scrollWidth -
						(scrollDiv.offsetWidth +
							this.additionalScrollerPadding);
					result = scrollDiv.scrollLeft >= width;
				}
			}
			return result;
		},
		onWheel(e) {
			if (e && e.deltaY < 0) {
				this.scrollingUp = true;
			} else {
				this.scrollingUp = false;
			}

			this.onScroll(e);
		},
		onScroll(e) {
			if (
				this.hasMoreResults &&
				this.windowBoundary() &&
				!this.scrollingUp
			) {
				if (!this.loading) {
					this.$emit('scrollBoundary', e);
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar('borderless');

.infinite-scroller {
	/* For Dev, the infinite-scroller need to have a variable height, */
	overflow-y: auto;
	flex-grow: 1; /* UI/UX note: this is needed for the scroller to fill the containing element */

	&__container-elements {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		margin-top: $gutter-xl;
		margin-bottom: $gutter-xl;
	}

	&__loading {
		position: relative;
		height: 40px;
		width: 100%;
		display: flex;
		align-items: center;
	}

	&__end-result {
		margin: 0 auto;
		width: 50px;
		text-align: center;
		color: var(--bg-0-fg-4);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
	}
	&__text {
		white-space: nowrap;
		font-size: $txt-supporting-font-size;
	}

	&__img {
		width: 50px;
		height: auto;
		margin-top: $gutter-md;
	}
}
</style>
